// SEQ-180 pure rename detection for the P&L category alias write
// (EditExpenseCategoriesModal.tsx, features/pnlAnalytics/aliasWrite.ts).
//
// CRITICAL FIX (round 2 code review). The previous version compared
// localCategories[i] against categories[i] by array index alone. Deleting
// any category that is not last shifts every later entry down one index, so
// the next save aliased an unrelated string to the wrong metric key forever
// (delete "Engineers" at index 2, and "Handrails & Stairs" got aliased to
// Engineering's key). The safe rule: only ever call something a rename when
// the two lists are the same LENGTH (so index really does correspond to the
// same underlying row) AND every other field on that entry (group,
// is_profit_deduction) is unchanged. A name change alongside a length
// difference, or alongside another field change, writes NO alias at all. A
// genuinely new or ambiguous string then falls through to expense.unmapped
// (plan decision D4), the safe default, and shows on the admin unmapped list
// (PnlAdminUnmapped.tsx) rather than silently mis-tagging history.
//
// CategoryLike is declared locally rather than imported from
// ../../hooks/usePnlCategories, mirroring src/lib/pnl/pnlCalculationCore.ts's
// own header comment: Deno's type-checker resolves a type's declaration by
// loading its whole file, and usePnlCategories.ts transitively imports
// ToastModal.tsx (document) and supabase.ts (import.meta.env), neither
// available under Deno. This interface is structurally identical to
// PnlCategory, so no behaviour changes for the real caller.

export interface CategoryLike {
  name: string;
  is_profit_deduction: boolean;
  group: "Wages" | "Materials" | "Other";
  /** SEQ-182 "Include in Financial P&L". Optional for the same reason it is optional on PnlCategory: undefined means true. */
  financial_inclusion?: boolean;
}

export interface CategoryRename {
  oldName: string;
  newName: string;
}

/**
 * Detects safe renames between the category list as loaded (`before`) and
 * the category list about to be saved (`after`), by original array
 * position. Returns an empty array whenever the lists differ in length (an
 * add, a delete, or both happened, and index correspondence cannot be
 * trusted) or whenever a name-changed entry also changed group or deduction,
 * since that is at least as likely to be a delete-and-recreate as a rename.
 */
/** Local copy of pnlCalculationCore's categoryEntersFinancialPnl: importing it would pull the whole engine into a module that only compares two lists. */
function entersFinancialPnl(category: CategoryLike): boolean {
  return category.financial_inclusion !== false;
}

export function detectCategoryRenames(before: CategoryLike[], after: CategoryLike[]): CategoryRename[] {
  if (before.length !== after.length) return [];

  const renames: CategoryRename[] = [];
  for (let i = 0; i < before.length; i++) {
    const priorEntry = before[i];
    const currentEntry = after[i];
    const oldName = priorEntry.name.trim();
    const newName = currentEntry.name.trim();

    if (!oldName || !newName || oldName === newName) continue;
    if (priorEntry.group !== currentEntry.group) continue;
    if (priorEntry.is_profit_deduction !== currentEntry.is_profit_deduction) continue;
    // financial_inclusion joins group and is_profit_deduction as a field
    // that, if it moved in the same save, makes this at least as likely a
    // delete-and-recreate as a rename. Compared through the shared default
    // so undefined and true are the same answer, never a phantom change.
    if (entersFinancialPnl(priorEntry) !== entersFinancialPnl(currentEntry)) continue;

    renames.push({ oldName, newName });
  }
  return renames;
}
